import * as React from 'react';
import {
  Box,
  Button,
  Cluster,
  Cover,
  Divider,
  Expand,
  FormItem,
  Heading,
  Icon,
  ISelectOption,
  Notification,
  Pagination,
  Paragraph,
  Select,
  Sidebar,
  Spinner,
  Stack,
  Table,
  TdLink,
  TdTextStatus,
  Template,
  TextInput,
  useValidateForm,
} from '@pluto-tv/assemble';
import {map, times} from 'lodash-es';
import {useHistory} from 'react-router-dom';
import {DateTime as luxon} from 'luxon';

import {useAppPermissions} from 'app/permissions';
import {useFindQuery as useFindPartnersQuery} from 'features/partners/partnersApi';

import NotAuthorized from 'components/notAuthorized';
import CrudError from 'components/crudError';
import {FormSearchWrapper} from 'components/formSearchWrapper';

import {useFindQuery} from 'features/runLimitTrackers/runLimitTrackersApi';

import {useUserRegions} from 'helpers/useUserRegions';
import {programmingTypes} from 'helpers/programmingTypes';
import {searchRunLimitsValidator} from './validators';
import runLimitsRoutes from 'routes/runLimits.routes';

const VALID_YEARS = map(times(10, num => `${2022 + num}`));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface ISearch {
  partnerId?: string;
  activeRegions?: string[];
  title?: string;
  month: string;
  year: string;
  programmingType?: string;
  licenseFee?: number;
}

const TABLE_COLUMN_NAME = {
  Title: 1,
  LF: 2,
  Partner: 3,
  'Active Region(s)': 4,
  Max: 5,
  Scheduled: 6,
} as const;

export default (): JSX.Element => {
  const history = useHistory();
  const {ableTo} = useAppPermissions();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [isSearchExpanded, setIsSearchExpanded] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [skipSearch, setSkipSearch] = React.useState(true);

  const [searchObj, setSearchObj] = React.useState<ISearch>({
    month: luxon.now().toFormat('M'),
    year: luxon.now().toFormat('yyyy'),
  });

  const [sortCol, setSortCol] = React.useState<keyof typeof TABLE_COLUMN_NAME>('Title');
  const [sortDir, setSortDir] = React.useState<1 | 2>(1);

  const {
    data: runLimitTrackers,
    isFetching,
    isError,
  } = useFindQuery(
    {
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      sortfield: TABLE_COLUMN_NAME[sortCol],
      sortdirection: sortDir,
      ...searchObj,
    },
    {refetchOnMountOrArgChange: true, skip: skipSearch},
  );

  const {data: partners, isFetching: isFetchingPartners, isError: isErrorPartners} = useFindPartnersQuery();
  const {activeRegions, isError: isRegionsError, isFetching: isRegionsFetching} = useUserRegions();

  const {
    form: searchForm,
    model: searchModel,
    onBlur: searchOnBlur,
    onChange: searchOnChange,
    setModel: searchSetModel,
    state: searchState,
    setFields: searchSetFields,
  } = useValidateForm(searchRunLimitsValidator, 'immediate');

  React.useEffect(
    () =>
      searchSetModel({
        month: luxon.now().toFormat('MMM'),
        year: luxon.now().toFormat('yyyy'),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  React.useEffect(() => {
    if (activeRegions && activeRegions.length > 0) {
      if (!searchObj.activeRegions || searchObj.activeRegions.length === 0) {
        setSearchObj({
          ...searchObj,
          activeRegions: activeRegions.map(region => region.code.toLowerCase()),
        });
      }

      setSkipSearch(false);
    } else {
      setSkipSearch(true);
    }
  }, [searchObj, activeRegions]);

  const search = (clear = false) => {
    setSkipSearch(true);

    if (clear) {
      searchSetModel({
        month: luxon.now().toFormat('MMM'),
        year: luxon.now().toFormat('yyyy'),
      });
      searchSetFields({
        licenseFee: undefined,
        activeRegions: undefined,
        partnerId: undefined,
        programmingType: undefined,
        title: undefined,
        month: luxon.now().toFormat('MMM'),
        year: luxon.now().toFormat('yyyy'),
      });
      setSearchObj({
        month: luxon.now().toFormat('M'),
        year: luxon.now().toFormat('yyyy'),
      });
    } else {
      setSearchObj({
        year: searchModel.year!,
        month: luxon.fromFormat(searchModel.month!, 'MMM').toFormat('M'),
        title: searchModel.title,
        partnerId: searchModel.partnerId,
        activeRegions: (searchModel.activeRegions || []).map(region => region.toLowerCase()),
        licenseFee: searchModel.licenseFee,
        programmingType: searchModel.programmingType,
      });
    }

    setSortCol('Title');
    setSortDir(1);
    setPage(0);
  };

  const changeSort = (columnName: keyof typeof TABLE_COLUMN_NAME) => {
    if (columnName === sortCol) {
      if (sortDir === 1) {
        setSortDir(2);
      } else {
        setSortDir(1);
      }
    } else {
      setSortCol(columnName);
      setSortDir(1);
    }

    setPage(0);
  };

  React.useEffect(() => {
    setIsLoading(isFetching || isFetchingPartners || isRegionsFetching);
  }, [isFetching, isFetchingPartners, isRegionsFetching]);

  if (!ableTo('RUN_LIMIT_TRACKER_VIEW')) {
    return <NotAuthorized />;
  }

  if (isError || isErrorPartners || isRegionsError) {
    return <CrudError error='Error loading page data' />;
  }

  return (
    <Sidebar fullHeight={true}>
      <Expand width='18.75rem' height='100%' fullHeightContainer={true} isExpanded={isSearchExpanded}>
        <Template label='expandable'>
          <Box background='pewter' padding='medium' fullHeight={true}>
            <Cover scrolling={true} gutter='medium'>
              <Template label='header'>
                <Stack space='medium'>
                  <Cluster align='center' justify='space-between'>
                    <Icon icon='tune' space='small' size='large' iconAlign='center'>
                      <Heading level='h4'>Search Filters</Heading>
                    </Icon>
                    <Icon icon='collapseleft' size='large' onClick={() => setIsSearchExpanded(!isSearchExpanded)} />
                  </Cluster>
                  <Divider color='graphite' />
                </Stack>
              </Template>
              <Template label='cover'>
                <Stack space='small'>
                  <Cluster wrap={false} space='xxxsmall' justify='space-between' fullWidth={true}>
                    <FormItem {...searchForm.month}>
                      <Select
                        placeholder='Month'
                        options={MONTHS.map(month => ({label: month}))}
                        onChange={val => searchSetFields({month: (val as ISelectOption)?.label})}
                        value={{label: searchModel.month || ''}}
                      />
                    </FormItem>
                    <FormItem {...searchForm.year}>
                      <Select
                        placeholder='Year'
                        options={VALID_YEARS.map(year => ({label: year}))}
                        onChange={val => searchSetFields({year: (val as ISelectOption)?.label})}
                        value={{label: searchModel.year || ''}}
                      />
                    </FormItem>
                  </Cluster>
                  <FormSearchWrapper onSearch={search}>
                    <FormItem {...searchForm.title} onBlur={() => searchOnBlur('title')}>
                      <TextInput
                        placeholder='Title'
                        value={searchModel.title}
                        onChange={val => searchOnChange('title', val)}
                      />
                    </FormItem>
                  </FormSearchWrapper>
                  <FormItem label='Partner'>
                    <Select
                      clearable={true}
                      id='partner'
                      predicate='value'
                      searchable={true}
                      onSearch={val =>
                        (partners || [])
                          .filter(partner => partner.name.toLowerCase().indexOf(val.toLowerCase()) > -1)
                          .map(partner => ({label: partner.name, value: partner.id})) || []
                      }
                      placeholder='Partner'
                      options={(partners || []).map(partner => ({label: partner.name, value: partner.id}))}
                      onChange={val => searchSetFields({partnerId: (val as ISelectOption)?.value})}
                      value={{label: '', value: searchModel.partnerId}}
                    />
                  </FormItem>
                  <FormItem label='Region'>
                    <Select
                      clearable={true}
                      options={activeRegions.map(ar => ({
                        label: `${ar.name} (${ar.code})`,
                        value: ar.code,
                      }))}
                      placeholder='Region(s)'
                      multiselect={true}
                      onChange={value => {
                        const regions = value as ISelectOption[];

                        searchSetFields({
                          activeRegions: (regions || []).map(region => region.value),
                        });
                      }}
                      predicate='value'
                      value={(searchModel.activeRegions || []).map(region => ({label: '', value: region}))}
                    />
                  </FormItem>
                  <FormItem label='Programming Type'>
                    <Select
                      clearable={true}
                      placeholder='Select a Programming Type'
                      onChange={value => searchSetFields({programmingType: (value as ISelectOption)?.label})}
                      value={{label: searchModel.programmingType || ''}}
                      options={programmingTypes.map(pT => ({label: pT}))}
                    />
                  </FormItem>
                  <FormSearchWrapper onSearch={search}>
                    <FormItem {...searchForm.licenseFee} onBlur={() => searchOnBlur('licenseFee')}>
                      <TextInput
                        type='number'
                        placeholder='License Fee'
                        value={searchModel.licenseFee}
                        onChange={val => searchOnChange('licenseFee', val)}
                      />
                    </FormItem>
                  </FormSearchWrapper>
                </Stack>
              </Template>
              <Template label='footer'>
                <Cluster justify='space-between'>
                  <div></div>
                  <Cluster space='xxxsmall'>
                    <Button state={isFetching ? 'disabled' : ''} onClick={() => search(true)}>
                      Clear
                    </Button>
                    <Button
                      type='primary'
                      state={!searchState.isValid ? 'disabled' : isFetching ? 'thinking' : ''}
                      onClick={() => search()}
                    >
                      Search
                    </Button>
                  </Cluster>
                </Cluster>
              </Template>
            </Cover>
          </Box>
        </Template>
      </Expand>
      <Cover
        scrolling={true}
        gutter='large'
        coverTemplateHeight='100%'
        overflow='auto'
        padding={{mobile: 'medium', wide: 'large'}}
      >
        <Template label='header'>
          <Cluster justify='space-between' align='center' space='medium'>
            <Cluster align='end' space='small'>
              <Heading level='h1'>
                Run Limit Tracker - {luxon.fromFormat(searchObj.month, 'M').toFormat('MMM')}. {searchObj.year}
              </Heading>
              <Cluster space='xxsmall' align='center'>
                <Icon
                  icon='tune'
                  space='xxxsmall'
                  verticalAlign='bottom'
                  lineHeight='0px'
                  onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                >
                  {runLimitTrackers?.metadata.totalCount || 0} Items
                </Icon>
              </Cluster>
            </Cluster>
          </Cluster>
        </Template>
        <Template label='cover'>
          <Box
            background='pewter'
            borderTop={true}
            borderSize='2px'
            borderColor='cavern'
            paddingTop={((runLimitTrackers?.data?.length || 0) as number) > 0 ? 'xsmall' : 'medium'}
            paddingBottom='none'
            paddingX='large'
            fullHeight={true}
          >
            <Table
              loading={isLoading}
              fixedHeader={true}
              fixedFirstCol={true}
              id='rlt-table'
              wrapContent={true}
              onSort={column => changeSort(column as keyof typeof TABLE_COLUMN_NAME)}
              sortDir={sortDir === 1 ? 'asc' : 'dsc'}
              sortCol={sortCol}
              titleCols={[
                {
                  colSpan: 4,
                  label: '',
                },
                {
                  colSpan: 3,
                  label: 'RUNS',
                },
              ]}
              cols={[
                {
                  fixed: true,
                  label: 'Title',
                  sortable: true,
                  colMinWidth: '400px',
                  transform: row => (
                    <TdLink
                      title={row.title}
                      row={row}
                      onClick={() =>
                        history.push(runLimitsRoutes.paths.licensedTitlesEditPage.replace(':id', row.licensedTitleId))
                      }
                    />
                  ),
                },
                {
                  label: 'LF',
                  sortable: true,
                  field: 'licenseFee',
                },
                {
                  label: 'Partner',
                  field: 'partnerName',
                  sortable: true,
                },
                {
                  label: 'Active Region(s)',
                  transform: row => row.activeRegion.toUpperCase(),
                  sortable: true,
                },
                {
                  label: 'Max',
                  transform: row => `${row.runs?.max}` || 'N/A',
                  sortable: true,
                },
                {
                  label: 'Scheduled',
                  transform: row => (
                    <TdTextStatus
                      state={
                        row.runs.max > -1 ? (row.runs?.scheduled > row.runs?.max ? 'error' : 'success') : undefined
                      }
                      title={`${row.runs?.scheduled}` || 'N/A'}
                    />
                  ),
                  sortable: true,
                },
                {
                  label: 'Aired',
                  transform: row => (
                    <TdTextStatus
                      state={row.runs.max > -1 ? (row.runs?.aired > row.runs?.max ? 'error' : 'success') : undefined}
                      title={`${row.runs?.aired}` || 'N/A'}
                    />
                  ),
                },
              ]}
              rows={runLimitTrackers?.data || []}
            >
              <Template label='loading'>
                <Cluster space='small' align='center'>
                  <Spinner />
                  <Paragraph>Loading Run Limit Data</Paragraph>
                </Cluster>
              </Template>
              <Template label='empty'>
                <Notification type='warning'>
                  <Paragraph>There is no Run Limit Data currently available.</Paragraph>
                </Notification>
              </Template>
            </Table>
          </Box>
        </Template>
        <Template label='footer'>
          <Cluster justify='space-between'>
            <div></div>
            {((runLimitTrackers?.data?.length || 0) as number) > 0 && (
              <Pagination
                perPage={rowsPerPage as 25 | 50 | 75 | 100}
                currentPage={page}
                total={runLimitTrackers?.metadata.totalCount || 0}
                onPageChange={page => {
                  setPage(page);
                  document.getElementById('rlt-table')?.parentElement?.scrollTo(0, 0);
                }}
                onPerPageChange={perPage => {
                  setRowsPerPage(perPage);
                  setPage(0);
                }}
              />
            )}
          </Cluster>
        </Template>
      </Cover>
    </Sidebar>
  );
};
